import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {AlertTriangle, CheckCircle2, Calendar, ArrowRight, Shield, Thermometer, Users, FileText} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const HajjUmrah = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const healthRisks = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Crowd-Related Infections",
      desc: "Large gatherings increase risk of respiratory and meningococcal infections."
    },
    {
      icon: <Thermometer className="w-6 h-6" />,
      title: "Heat-Related Illness",
      desc: "Extreme temperatures and physical exertion can lead to dehydration and heat stroke."
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: "Food & Water Safety",
      desc: "Travellers' diarrhea and foodborne illness are common without proper precautions."
    }
  ];

  const vaccinesDiscuss = [
    { name: "Meningococcal ACWY", priority: "Required", note: "Mandatory for Saudi visa (within last 5 years)" },
    { name: "Influenza (Flu)", priority: "Highly Recommended", note: "Reduces risk of respiratory illness in crowds" },
    { name: "COVID-19", priority: "Recommended", note: "Check current Saudi Arabia entry requirements" },
    { name: "Hepatitis A", priority: "Recommended", note: "Protection against contaminated food/water" },
    { name: "Hepatitis B", priority: "Consider", note: "For those at higher risk of exposure" },
    { name: "Typhoid", priority: "Consider", note: "Additional protection for food/water safety" },
    { name: "MMR (Measles/Mumps/Rubella)", priority: "Ensure Up-to-Date", note: "Measles outbreaks can occur in large gatherings" },
    { name: "Polio", priority: "Ensure Up-to-Date", note: "May be required depending on origin country" }
  ];

  const medications = [
    "Travellers' diarrhea treatment kit (antibiotics and anti-motility)",
    "Oral rehydration salts for dehydration management",
    "Pain relief and fever reducers (acetaminophen/ibuprofen)",
    "Antihistamines for allergic reactions",
    "Personal prescription medications in original packaging"
  ];

  const healthTips = [
    {
      icon: <Shield className="w-5 h-5" />,
      tip: "Stay Hydrated",
      detail: "Drink 2-3 liters of water daily, especially during outdoor rituals."
    },
    {
      icon: <FileText className="w-5 h-5" />,
      tip: "Hand Hygiene",
      detail: "Wash hands frequently and use alcohol-based sanitizer regularly."
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      tip: "Wear Face Masks",
      detail: "Consider wearing masks in crowded areas to reduce respiratory infection risk."
    },
    {
      icon: <Thermometer className="w-5 h-5" />,
      tip: "Monitor Heat",
      detail: "Rest in shade, avoid peak sun hours (11am-3pm), watch for heat exhaustion signs."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-amber-600 via-orange-600 to-amber-700 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1591604129939-f1efa4d9f7fa?auto=format&fit=crop&w=1920&q=80"
            alt="Kaaba in Mecca"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium mb-6">
              Religious Pilgrimage • Saudi Arabia
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Hajj & Umrah Travel Health
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Specialized health planning for your sacred journey. Our travel health experts help you meet Saudi Arabia's health requirements and stay safe during your pilgrimage.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-orange-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Hajj/Umrah Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Key Health Risks */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Key Health Risks to Discuss
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Understanding potential health challenges helps you prepare for a safe and spiritually fulfilling journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {healthRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-navy-900 rounded-2xl shadow-lg border border-gray-100 dark:border-white/5"
              >
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-xl flex items-center justify-center text-orange-600 dark:text-orange-400 mb-4">
                  {risk.icon}
                </div>
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{risk.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{risk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vaccines & Medications */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vaccines */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Vaccines to Discuss with Your Clinician
              </h2>
              <div className="space-y-4">
                {vaccinesDiscuss.map((vaccine, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-gray-50 dark:bg-navy-800 rounded-xl border border-gray-100 dark:border-white/5 hover:border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-navy-900 dark:text-white">{vaccine.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        vaccine.priority === "Required" 
                          ? "bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                          : vaccine.priority === "Highly Recommended"
                          ? "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400"
                          : "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                      }`}>
                        {vaccine.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{vaccine.note}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-200 dark:border-cyan-800 rounded-xl">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-cyan-900 dark:text-cyan-100">
                    <strong>Important:</strong> Meningococcal ACWY vaccination is mandatory for Saudi visa approval and must have been administered within the last 5 years but at least 10 days before arrival.
                  </div>
                </div>
              </div>
            </div>

            {/* Medications & Supplies */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Medications & Health Supplies
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your clinician may recommend packing the following items for your journey:
              </p>
              <ul className="space-y-3 mb-8">
                {medications.map((med, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{med}</span>
                  </motion.li>
                ))}
              </ul>

              <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-4">Essential Health Tips</h3>
              <div className="space-y-3">
                {healthTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-navy-800 rounded-lg">
                    <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                      {tip.icon}
                    </div>
                    <div>
                      <div className="font-bold text-navy-900 dark:text-white text-sm">{tip.tip}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{tip.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              When to Book Your Consultation
            </h2>
            <p className="text-gray-300 text-lg">
              Timing is crucial for Hajj and Umrah travel health preparation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">6-8 Weeks</div>
              <div className="text-white font-medium mb-2">Ideal Timeline</div>
              <div className="text-sm text-gray-400">Allows time for multi-dose vaccines and visa processing</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-orange-400 mb-2">4 Weeks</div>
              <div className="text-white font-medium mb-2">Minimum Time</div>
              <div className="text-sm text-gray-400">Latest recommended booking for full protection</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-red-400 mb-2">10 Days</div>
              <div className="text-white font-medium mb-2">Visa Requirement</div>
              <div className="text-sm text-gray-400">Meningococcal vaccine must be given at least 10 days before arrival</div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Your Hajj/Umrah Consultation
              <ArrowRight size={20} />
            </button>
            <p className="text-gray-400 text-sm mt-4">
              Virtual consultations available • $60 CAD • Same-day prescriptions
            </p>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default HajjUmrah;
